import {
  Component,
  signal,
  inject,
  computed, input,
  OnInit, OnDestroy,
  effect, output
} from '@angular/core';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { LiveAnnouncer } from "@angular/cdk/a11y";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Subscription } from 'rxjs';
import { IKeywordDetails } from "../../interfaces/i-keyword";

/**
 * This component provides a user interface for managing a list of keywords within a parent form.
 * It acts as an adapter between the backend (string[] of titles) and the UI (IKeywordDetails[] objects).
 *
 * The component:
 * - Reads string[] from the parent form
 * - Converts to IKeywordDetails[] for rich display (labels, types, descriptions)
 * - Converts back to string[] when updating the form
 * - Allows users to add, remove, and select keywords using chips and autocomplete
 */
@Component({
  selector: 'keywords-controller',
  standalone: true,
  imports: [
    MatAutocompleteModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
  templateUrl: './keywords-controller.component.html',
  styleUrls: ['./keywords-controller.component.scss']
})
export class KeywordsControllerComponent implements OnInit, OnDestroy {
  parentForm = input.required<FormGroup>();

  // All available keywords as rich IKeywordDetails objects
  allKeywords = input<IKeywordDetails[]>([]);

  // Internal state: selected keywords as IKeywordDetails[] for display
  readonly #selectedKeywordObjects = signal<IKeywordDetails[]>([]);

  // Expose selected keywords for template
  readonly selectedKeywords = computed(() => this.#selectedKeywordObjects());

  readonly announcer = inject(LiveAnnouncer);
  readonly separatorKeysCodes: number[] = [ ENTER, COMMA ];
  readonly keywordInputControl = new FormControl('');
  readonly keywordInputValue = signal('');

  // Output emits string[] to maintain backward compatibility
  keywordsChanged = output<string[]>();

  constructor() {
    this.keywordInputControl.valueChanges.subscribe(value => {
      this.keywordInputValue.set(value || '');
    });

    // Effect: Convert form's string[] to IKeywordDetails[] for display
    effect(() => {
      const keywordTitles = this.#getKeywordTitlesFromForm();
      const keywordObjects = this.#lookupKeywordsByTitles(keywordTitles);
      this.#selectedKeywordObjects.set(keywordObjects);
    });
  }

  // Filtered keywords for autocomplete (excludes already selected)
  readonly filteredKeywords = computed(() => {
    const inputValue = this.keywordInputValue().toLowerCase();
    const selected = this.#selectedKeywordObjects();

    return this.allKeywords().filter(keyword => {
      // Filter out already selected keywords
      const isSelected = selected.some(s => s.id === keyword.id);
      if (isSelected) return false;

      // Apply search filter
      if (!inputValue) return true;

      return (
        keyword.title.toLowerCase().includes(inputValue) ||
        keyword.label?.toLowerCase().includes(inputValue) ||
        keyword.type.toLowerCase().includes(inputValue) ||
        keyword.typeDescription?.toLowerCase().includes(inputValue) ||
        keyword.category?.toLowerCase().includes(inputValue)
      );
    });
  });

  #keywordsSub: Subscription | undefined;

  get keywordsControl(): FormControl | undefined {
    return this.parentForm().get('keywords') as FormControl | undefined;
  }

  ngOnInit(): void {
    // Keep chips in sync with parent form (for legacy/other updates)
    this.#keywordsSub = this.parentForm().get('keywords')?.valueChanges.subscribe(val => {
      const titles = this.#normalizeKeywordsValue(val);
      const keywordObjects = this.#lookupKeywordsByTitles(titles);
      this.#selectedKeywordObjects.set(keywordObjects);
    });
  }

  ngOnDestroy(): void {
    this.#keywordsSub?.unsubscribe();
  }

  /**
   * Adds a keyword to the list by typing and pressing enter/comma.
   * Looks up the keyword by title in allKeywords.
   * @param event The MatChipInputEvent containing the input value.
   */
  addKeyword(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (!value) {
      event.chipInput.clear();
      return;
    }

    // Look up keyword by title
    const keyword = this.allKeywords().find(k =>
      k.title.toLowerCase() === value.toLowerCase()
    );

    if (keyword) {
      const isAlreadySelected = this.#selectedKeywordObjects().some(k => k.id === keyword.id);

      if (!isAlreadySelected) {
        this.#addKeywordObject(keyword);
      }
    }

    event.chipInput.clear();
    this.keywordInputControl.setValue('');
  }

  /**
   * Removes a keyword from the list.
   * @param keyword The IKeywordDetails object to remove.
   */
  removeKeyword(keyword: IKeywordDetails): void {
    this.#selectedKeywordObjects.update((keywords) => keywords.filter((k) => k.id !== keyword.id));

    // Convert back to string[] for form
    const titles = this.#selectedKeywordObjects().map(k => k.title);
    this.parentForm().get('keywords')?.setValue(titles);
    this.announcer.announce(`Removed ${keyword.title}`);
    this.keywordsChanged.emit(titles);
  }

  /**
   * Handles selection of a keyword from the autocomplete dropdown.
   * @param event The MatAutocompleteSelectedEvent containing the selected option.
   */
  selectedKeyword(event: MatAutocompleteSelectedEvent): void {
    const keyword = event.option.value as IKeywordDetails;

    if (keyword) {
      this.#addKeywordObject(keyword);
    }

    this.keywordInputControl.setValue('');
    event.option.deselect();
  }

  /**
   * Display function for autocomplete - shows keyword label and title.
   */
  displayKeyword(keyword: IKeywordDetails | null): string {
    if (!keyword) return '';
    return keyword.label ? `${keyword.label} - ${keyword.title}` : keyword.title;
  }

  // Private helper methods

  /**
   * Adds a keyword object to the selected list and updates the form.
   */
  #addKeywordObject(keyword: IKeywordDetails): void {
    this.#selectedKeywordObjects.update((keywords) => [...keywords, keyword]);

    // Convert to string[] for form
    const titles = this.#selectedKeywordObjects().map(k => k.title);
    this.parentForm().get('keywords')?.setValue(titles);
    this.keywordsChanged.emit(titles);
  }

  /**
   * Gets keyword titles from the parent form as string[].
   */
  #getKeywordTitlesFromForm(): string[] {
    const val = this.parentForm().get('keywords')?.value;
    return this.#normalizeKeywordsValue(val);
  }

  /**
   * Normalizes form value to string[] (handles string, string[], or null/undefined).
   */
  #normalizeKeywordsValue(val: any): string[] {
    if (Array.isArray(val)) {
      return val;
    } else if (typeof val === 'string' && val) {
      return [val];
    } else {
      return [];
    }
  }

  /**
   * Looks up IKeywordDetails objects by their titles.
   * If a title doesn't exist in allKeywords (e.g., user-entered custom keyword),
   * creates a temporary IKeywordDetails object so it's not lost.
   * @param titles Array of keyword titles
   * @returns Array of IKeywordDetails objects
   */
  #lookupKeywordsByTitles(titles: string[]): IKeywordDetails[] {
    return titles.map(title => {
      // Try to find official keyword from backend
      const officialKeyword = this.allKeywords().find(k => k.title === title);

      if (officialKeyword) {
        return officialKeyword;
      }

      // Create temporary IKeywordDetails for custom/legacy keywords
      // This ensures user-entered keywords aren't lost
      return {
        id: `custom-${title}`, // Temporary ID
        title: title,
        type: 'Custom',
        typeDescription: 'User-entered keyword (not in official list)',
        isEnabled: true
      } as IKeywordDetails;
    });
  }
}
